// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskUrn, UrlEncodedUrn } from '@evolvemep/api-ts';

import type { AutodeskStoreBase } from 'components/Autodesk/useAutodeskExtensionStore';

/**
 * Recursively find a `BubbleNode` that matches `predicate`, starting at `node`
 * and then checking the node's children until a match is found.
 */
export const findDocumentNode = (
  node: Autodesk.Viewing.BubbleNode,
  predicate: (node: Autodesk.Viewing.BubbleNode) => boolean,
): Autodesk.Viewing.BubbleNode | undefined => {
  if (predicate(node)) return node;
  for (const child of node.children) {
    const found = findDocumentNode(child, predicate);
    if (found) return found;
  }
  return undefined;
};

/**
 * Access the lineage URN of the model loaded in the viewer if available, which matches the format of our saved model URNs.
 * Uses a type assertion to access an untyped property of the viewer's model loader.
 */
export const getLineageUrn = (
  viewerModel: (Autodesk.Viewing.Model & { loader?: { _lineageUrn: AutodeskUrn } }) | undefined,
): AutodeskUrn | null => viewerModel?.loader?._lineageUrn ?? null;

type ViewingNamespace = typeof Autodesk.Viewing;

type ViewerEventName = {
  [K in keyof ViewingNamespace]: ViewingNamespace[K] extends string
    ? string extends ViewingNamespace[K]
      ? never
      : ViewingNamespace[K]
    : never;
}[keyof ViewingNamespace];

/**
 * Add an event listener to the viewer and return a function to remove it.
 */
export const addViewerListener = <E>(
  viewer: Autodesk.Viewing.GuiViewer3D,
  event: ViewerEventName,
  callback: (event: E) => void,
) => {
  viewer.addEventListener(event, callback);
  return () => viewer.removeEventListener(event, callback);
};

/**
 * Monitors part of a store's snapshot for changes. On changes, calls the provided callback with the new state.
 * @returns a cleanup callback to stop listening for updates.
 */
export const monitorStoreState = <T, UpdatedState>(
  store: AutodeskStoreBase<T>,
  /**
   * A function that extracts the part of the store's snapshot to monitor.
   * Changes are detected using `Object.is`, so make sure the callback doesn't return a new object.
   **/
  getStatePart: (snapshot: T) => UpdatedState,
  /** A callback, called with the new state when it changes. */
  onStateChange: (newState: UpdatedState) => void,
) => {
  let lastState = getStatePart(store.getSnapshot());
  return store.subscribe(() => {
    const newState = getStatePart(store.getSnapshot());
    if (Object.is(newState, lastState)) return;

    lastState = newState;
    onStateChange(newState);
  });
};

export const urlEncodeUrn = (urn: AutodeskUrn): UrlEncodedUrn => encodeURIComponent(urn) as UrlEncodedUrn;
